import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { X, GripVertical, FileText } from "lucide-react";
import { formatFileSize } from "@/lib/formatFileSize";
import { reorderList, rowIndexAtY, findDuplicateFiles } from "./reorderMath";

// Kept exported from here: other tools import it from this module.
export { findDuplicateFiles };

export interface ReorderableFileListProps {
  files: File[];
  onReorder: (newFiles: File[]) => void;
  onRemove: (index: number) => void;
  /** Optional icon override per item; defaults to a generic file icon.
   * Ignored for a given item when thumbnailUrls provides a real preview
   * for that same index instead. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Optional per-file preview image URLs (e.g. object URLs from
   * URL.createObjectURL), same length/order as files. When provided,
   * shows a real thumbnail instead of the generic icon -- lets a caller
   * like Image to PDF show what's actually in the list instead of just
   * filenames, without changing behavior for callers that don't pass
   * this (Merge PDF and others keep the plain icon they already had). */
  thumbnailUrls?: (string | null)[];
  /** How a thumbnail fills its box. "cover" (default) crops to a small square -- right for photos.
   * "page" shows the whole page in a taller portrait box -- for documents, where a square crop of a
   * mostly-white page's corner looked like a blank rectangle (QA MP-13). */
  thumbnailFit?: "cover" | "page" | "large";
  /** Optional per-file rotation in degrees (0/90/180/270), same length
   * as files -- applied as a CSS transform on the thumbnail so a
   * rotation choice is visible immediately, not just reflected in the
   * final output. */
  thumbnailRotations?: number[];
  /** Optional extra content rendered per item, just before the
   * up/down/remove controls -- e.g. a rotate button for Image to PDF.
   * Receives the item's index so a caller can wire it to per-file
   * state. Omitted entirely for callers that don't need it. */
  renderItemExtra?: (index: number) => React.ReactNode;
  /** Extra per-file buttons placed in the control group (before up/down/remove). Setting this also switches the
   * phone layout to wrapping file names and a 2x2 grid of 44px buttons; from `sm` up the row is unchanged. */
  rowActions?: (index: number) => React.ReactNode;
}

/**
 * A real, working drag-to-reorder file list -- used by any tool where
 * multiple files are combined and their order matters (Merge PDF, Image to
 * PDF, and similar). Built once here so every tool that needs this gets
 * the same tested behavior instead of a decorative grip icon with nothing
 * behind it.
 */
export function ReorderableFileList({ files, onReorder, onRemove, icon: Icon = FileText, thumbnailUrls, thumbnailFit = "cover", thumbnailRotations, renderItemExtra, rowActions }: ReorderableFileListProps) {
  const grid = !!rowActions;
  const btn = grid ? "h-11 w-11 sm:h-7 sm:w-7" : "h-9 w-9 sm:h-7 sm:w-7";
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  // Touch reordering. HTML5 drag-and-drop does not fire from a finger, so touch and pen drag the
  // grip handle with pointer events instead (the mouse keeps the native drag on the row). While a
  // touch drag is active the row is not `draggable`, so a long press can't also start the browser's
  // own drag and fight this one.
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const touchFrom = useRef<number | null>(null);
  const [touchDragging, setTouchDragging] = useState(false);

  const indexUnderPointer = (clientY: number) =>
    rowIndexAtY(rowRefs.current.slice(0, files.length).map((el) => el?.getBoundingClientRect() ?? { top: 0, bottom: 0 }), clientY);

  const gripPointerDown = (index: number) => (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.pointerType === "mouse") return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* capture is best-effort */
    }
    touchFrom.current = index;
    setTouchDragging(true);
    setDragIndex(index);
    setOverIndex(index);
  };
  const gripPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (touchFrom.current === null) return;
    const over = indexUnderPointer(e.clientY);
    if (over !== overIndex) setOverIndex(over);
  };
  const endTouchDrag = (e: React.PointerEvent<HTMLButtonElement> | null) => {
    const from = touchFrom.current;
    touchFrom.current = null;
    setTouchDragging(false);
    setDragIndex(null);
    setOverIndex(null);
    if (from === null || !e) return; // cancelled: leave the order alone
    const to = indexUnderPointer(e.clientY);
    if (to !== from) onReorder(reorderList(files, from, to));
  };

  const handleDragStart = (index: number) => (e: React.DragEvent) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (index !== overIndex) setOverIndex(index);
  };

  const handleDrop = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    onReorder(reorderList(files, dragIndex, index));
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  const moveByKeyboard = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= files.length) return;
    const reordered = [...files];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    onReorder(reordered);
  };

  return (
    <div className="space-y-2">
      {files.map((file, index) => (
        <div
          key={`${file.name}-${file.size}-${index}`}
          ref={(el) => { rowRefs.current[index] = el; }}
          draggable={!touchDragging}
          onDragStart={handleDragStart(index)}
          onDragOver={handleDragOver(index)}
          onDrop={handleDrop(index)}
          onDragEnd={handleDragEnd}
          className={`flex items-center ${grid ? "gap-2 sm:gap-3" : "gap-3"} p-3 rounded-lg border bg-card transition-colors ${
            dragIndex === index ? "opacity-50" : ""
          } ${overIndex === index && dragIndex !== index ? "border-primary" : "border-border"}`}
        >
          <button
            type="button"
            className="cursor-grab active:cursor-grabbing touch-none p-2 -m-2 sm:p-0 sm:m-0"
            aria-label={`Drag to reorder ${file.name}`}
            title="Drag to reorder"
            onPointerDown={gripPointerDown(index)}
            onPointerMove={gripPointerMove}
            onPointerUp={(e) => endTouchDrag(e)}
            onPointerCancel={() => endTouchDrag(null)}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </button>
          {thumbnailUrls?.[index] ? (
            <img
              src={thumbnailUrls[index]!}
              alt=""
              className={thumbnailFit === "page"
                ? "h-14 w-11 rounded-sm object-contain bg-white shrink-0 border border-border shadow-sm transition-transform"
                : thumbnailFit === "large"
                  ? "h-20 w-20 rounded object-contain bg-muted/40 shrink-0 border transition-transform"
                  : "h-10 w-10 rounded object-cover shrink-0 border transition-transform"}
              style={thumbnailRotations?.[index] ? { transform: `rotate(${thumbnailRotations[index]}deg)` } : undefined}
            />
          ) : (
            <Icon className="h-4 w-4 text-primary shrink-0" />
          )}
          {/* Name on top, size/extras beneath it on phones: with everything
              inline, a 390px screen left the filename about three
              characters ("qua...") once a page count was added. From `sm`
              up this is the same single row as before. */}
          <div className="flex-1 min-w-0 flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-3">
            <span className={`text-sm sm:flex-1 ${grid ? "break-words [overflow-wrap:anywhere] sm:truncate" : "truncate"}`} title={file.name}>{file.name}</span>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 sm:gap-3 sm:shrink-0">
              <span className="text-xs text-muted-foreground">{formatFileSize(file.size)}</span>
              {renderItemExtra?.(index)}
            </div>
          </div>
          <div className={grid ? "grid grid-cols-2 gap-1 shrink-0 sm:flex sm:items-center sm:gap-0.5" : "flex items-center gap-0.5"}>
            {rowActions?.(index)}
            <Button
              variant="ghost"
              size="icon"
              className={btn}
              disabled={index === 0}
              onClick={() => moveByKeyboard(index, -1)}
              aria-label="Move up"
              title="Move up"
            >
              ↑
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={btn}
              disabled={index === files.length - 1}
              onClick={() => moveByKeyboard(index, 1)}
              aria-label="Move down"
              title="Move down"
            >
              ↓
            </Button>
            <Button variant="ghost" size="icon" className={btn} onClick={() => onRemove(index)} aria-label={`Remove ${file.name}`} title="Remove">
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Detects files that look like exact duplicates (same name + size) among
 * files already selected, so a tool can warn before silently accepting
 * the same file twice. */

